// Curation is two decisions: one eligibility gate the curator can override
// check by check, then one AI match score the curator can reshape live. The
// sidebar stays visible throughout so the decision is always one click away.

import React, { useEffect, useRef, useState } from 'react';
import Alert from 'components/styleguide/alert/Alert';
import Icon from 'components/styleguide/icon/Icon';
import { reviewUrls, ServerReviewState } from './utils/curationApi';
import { CurationReviewProvider, FinishCurationPath, useCurationReview } from './state/CurationReviewContext';
import AiScorePanel from './AiScorePanel';
import DecisionPanel from './DecisionPanel';
import GateSection from './GateSection';
import PhaseStepper from './PhaseStepper';
import ProgressStrip from './ProgressStrip';
import RecruiterNotes from './RecruiterNotes';

const styles = require('./CurationReview.module.scss');

export interface Props {
  applicationId: number | string;
  candidateDetail?: React.ReactNode;
  canRequestHandpicked: boolean;
  curator?: string | null;
  finishCurationPath?: FinishCurationPath | null;
  header?: React.ReactNode;
  isCurated: boolean;
  reviewState: ServerReviewState | null;
}

function ReviewLayout({
  candidateDetail,
  canRequestHandpicked,
  header,
}: {
  candidateDetail?: React.ReactNode;
  canRequestHandpicked: boolean;
  header?: React.ReactNode;
}) {
  const { finishCurationPath, state } = useCurationReview();
  const wasSubmitted = useRef(state.submitted);
  const [detailOpen, setDetailOpen] = useState(false);

  // Speed for power users: a completed curation moves straight to the next
  // application in the queue, like the old flow did.
  useEffect(() => {
    if (state.submitted && !wasSubmitted.current && finishCurationPath) {
      window.location.assign(finishCurationPath.path);
    }
    wasSubmitted.current = state.submitted;
  }, [state.submitted, finishCurationPath]);

  return (
    <div className={styles['ui-curation-review']}>
      <ProgressStrip />
      <div className={styles['ui-curation-review__columns']}>
        <main className={styles['ui-curation-review__main']}>
          {header}
          <GateSection />
          <AiScorePanel />
          {candidateDetail && (
            <section
              aria-label="Candidate detail"
              className={styles['ui-curation-review__detail']}
            >
              <button
                aria-expanded={detailOpen}
                className={styles['ui-curation-review__detail-toggle']}
                onClick={() => setDetailOpen((open) => !open)}
                type="button"
              >
                <Icon aria-hidden name={detailOpen ? 'chevronDown' : 'chevron'} size="sm" />
                Full candidate detail
              </button>
              {detailOpen && (
                <div className={styles['ui-curation-review__detail-body']}>{candidateDetail}</div>
              )}
            </section>
          )}
        </main>
        <aside aria-label="Curation summary" className={styles['ui-curation-review__sidebar']}>
          <PhaseStepper />
          <RecruiterNotes />
          <DecisionPanel canRequestHandpicked={canRequestHandpicked} />
        </aside>
      </div>
    </div>
  );
}

export default function CurationReview({
  applicationId,
  candidateDetail,
  canRequestHandpicked,
  curator = null,
  finishCurationPath = null,
  header,
  isCurated,
  reviewState,
}: Props) {
  if (reviewState === null) {
    return (
      <div className={styles['ui-curation-review']}>
        {header}
        <Alert
          message={
            isCurated
              ? `Already screened${curator ? ` by ${curator}` : ''}.`
              : 'This application is not ready for curation.'
          }
          variant="info"
        />
        {candidateDetail}
      </div>
    );
  }

  return (
    <CurationReviewProvider
      experienceMax={reviewState.job.experienceMax}
      experienceMin={reviewState.job.experienceMin}
      finishCurationPath={finishCurationPath}
      initialReview={reviewState}
      urls={reviewUrls(applicationId)}
    >
      <ReviewLayout
        candidateDetail={candidateDetail}
        canRequestHandpicked={canRequestHandpicked}
        header={header}
      />
    </CurationReviewProvider>
  );
}
